import { Loader2, Mic, MicOff, RotateCcw, Sparkles } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "./ui/Button.jsx";
import { useToast } from "../hooks/useToast.js";
import { parseVoiceToLead } from "../services/leadService.js";

export default function VoiceLeadInput({ onParsed, disabled }) {
  const { toast } = useToast();
  const [state, setState] = useState("idle"); // idle, listening, processing, done, error
  const [transcript, setTranscript] = useState("");
  const [duration, setDuration] = useState(0);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      chunksRef.current = [];
      setTranscript("");
      setDuration(0);
      setState("listening");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      streamRef.current = stream;

      // Use webm if supported, fallback to mp4
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "audio/webm";

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        // Process audio after stop
        processAudio();
      };

      recorder.start(250); // Collect data every 250ms

      // Duration timer
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 500);

    } catch (err) {
      console.error("Mic access error:", err);
      setState("error");
      if (err.name === "NotAllowedError") {
        toast({
          variant: "destructive",
          title: "Microphone blocked",
          description: "Allow microphone access in your browser settings.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Could not access microphone",
          description: err.message,
        });
      }
    }
  }, [toast]);

  const stopRecording = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const processAudio = useCallback(async () => {
    setState("processing");

    const chunks = chunksRef.current;
    if (!chunks.length) {
      toast({ variant: "destructive", title: "No audio recorded" });
      setState("idle");
      return;
    }

    const mimeType = chunks[0].type || "audio/webm";
    const audioBlob = new Blob(chunks, { type: mimeType });

    // Check minimum size (avoid sending silence)
    if (audioBlob.size < 1000) {
      toast({ variant: "destructive", title: "Recording too short", description: "Please speak for at least 2 seconds." });
      setState("idle");
      return;
    }

    try {
      const res = await parseVoiceToLead(audioBlob);

      if (res.success && res.data) {
        setTranscript(res.transcript || "");
        onParsed?.(res.data);
        setState("done");
        toast({ title: "Lead data extracted from voice" });
      } else {
        setState("error");
        toast({ variant: "destructive", title: "Could not extract lead data" });
      }
    } catch (err) {
      console.error("Voice parse error:", err);
      setState("error");
      toast({ variant: "destructive", title: "Processing failed", description: err.message });
    }
  }, [onParsed, toast]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  const handleRetry = () => {
    setTranscript("");
    setDuration(0);
    setState("idle");
  };

  const formatDuration = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div className="space-y-2">
      {/* Button row */}
      <div className="flex items-center gap-2">
        {state === "idle" && (
          <Button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className="h-8 px-3 text-xs rounded-full gap-1.5 bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white"
          >
            <Mic size={14} />
            Add by Voice
          </Button>
        )}

        {state === "listening" && (
          <Button
            type="button"
            onClick={stopRecording}
            className="h-8 px-3 text-xs rounded-full gap-1.5 bg-red-500 hover:bg-red-600 text-white"
          >
            <MicOff size={14} />
            Stop ({formatDuration(duration)})
          </Button>
        )}

        {state === "processing" && (
          <div className="flex items-center gap-2 h-8 px-3 text-xs text-[#00a8b4]">
            <Loader2 size={14} className="animate-spin" />
            <span>Processing voice...</span>
          </div>
        )}

        {(state === "done" || state === "error") && (
          <Button
            type="button"
            onClick={handleRetry}
            variant="outline"
            className={`h-8 px-3 text-xs rounded-full gap-1.5 ${
              state === "error" ? "border-red-200 text-red-500" : ""
            }`}
          >
            <RotateCcw size={12} />
            {state === "error" ? "Retry" : "Try Again"}
          </Button>
        )}
      </div>

      {/* Recording indicator */}
      {state === "listening" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-xs font-medium text-red-700">Recording...</span>
            <span className="text-[10px] text-red-400">Tap Stop when done speaking</span>
          </div>
          {/* Audio visualizer dots */}
          <div className="flex items-center gap-1 mt-2">
            {[...Array(12)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-red-400 rounded-full animate-pulse"
                style={{
                  height: `${Math.random() * 16 + 4}px`,
                  animationDelay: `${i * 0.1}s`,
                  animationDuration: `${0.5 + Math.random() * 0.5}s`,
                }}
              />
            ))}
          </div>
        </div>
      )}

      {/* Processing */}
      {state === "processing" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <Sparkles size={12} className="text-blue-500" />
            <span className="text-[10px] font-medium text-blue-700">AI is transcribing and extracting lead data...</span>
          </div>
        </div>
      )}

      {/* Success */}
      {state === "done" && transcript && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-2">
          <p className="text-[10px] text-green-700">
            <span className="font-medium">Heard:</span> "{transcript}"
          </p>
        </div>
      )}

      {/* Error */}
      {state === "error" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2">
          <p className="text-[10px] text-red-600">
            Could not process voice. Tap Retry and speak clearly.
          </p>
        </div>
      )}
    </div>
  );
}
